import React, { useState, useEffect, useCallback } from 'react';
import { SERVICE_MANAGERS } from '../supportConfig';

const API_BASE = '';

export const RELOCATION_STATUSES = [
  { id:'Pending',             color:'#8E8E93', bg:'rgba(142,142,147,.1)', icon:'🕐' },
  { id:'Assigned to Manager', color:'#007AFF', bg:'rgba(0,122,255,.1)',   icon:'👤' },
  { id:'Assigned to CE',      color:'#AF52DE', bg:'rgba(175,82,222,.1)',  icon:'🔧' },
  { id:'In Progress',         color:'#FF9F0A', bg:'rgba(255,159,10,.1)',  icon:'⚙️' },
  { id:'Completed',           color:'#34C759', bg:'rgba(52,199,89,.1)',   icon:'✅' },
  { id:'Rejected',            color:'#FF3B30', bg:'rgba(255,59,48,.1)',   icon:'❌' },
];

export function relocStatusCfg(status) {
  return RELOCATION_STATUSES.find(s => s.id === status) || RELOCATION_STATUSES[0];
}

const STYLES = `
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  .reloc-row { transition:background .14s,box-shadow .14s; cursor:pointer; }
  .reloc-row:hover { background:#f8f9fc!important; box-shadow:0 4px 18px rgba(0,0,0,.08)!important; }
`;

const fmtDate = iso => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})
      + ', ' + d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  } catch { return iso; }
};

function StatusBadge({ status }) {
  const cfg = relocStatusCfg(status);
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:'3px 11px', borderRadius:20,
      background:cfg.bg, color:cfg.color, display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:cfg.color, flexShrink:0 }}/>
      {status || 'Pending'}
    </span>
  );
}

function KPICard({ label, value, color, icon, active, onClick, delay='0s' }) {
  return (
    <div onClick={onClick} style={{
      background: active ? color : '#fff',
      borderRadius:14, padding:'14px 16px', cursor:onClick?'pointer':'default',
      border: active ? `2px solid ${color}` : `1.5px solid ${color}22`,
      boxShadow: active ? `0 6px 20px ${color}33` : '0 2px 8px rgba(0,0,0,.05)',
      position:'relative', overflow:'hidden',
      animation:`fadeUp .35s ease ${delay} both`,
      transition:'all .2s',
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background: active?'rgba(255,255,255,.35)':color, borderRadius:'14px 14px 0 0' }}/>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:.4, textTransform:'uppercase',
          color: active?'rgba(255,255,255,.8)':'#8E8E93' }}>{label}</div>
        <span style={{ fontSize:16 }}>{icon}</span>
      </div>
      <div style={{ fontSize:28, fontWeight:900, color: active?'#fff':'#1c1c1e', letterSpacing:-.6, lineHeight:1 }}>
        {value}
      </div>
    </div>
  );
}

// ── Assign Manager Modal ─────────────────────────────────────────────────────
function AssignManagerModal({ request, onClose, onSaved }) {
  const [managerId, setManagerId] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error,  setError]        = useState('');

  const handleSave = async () => {
    if (!managerId) { setError('Please select a manager'); return; }
    setSaving(true); setError('');
    try {
      const mgr = SERVICE_MANAGERS.find(m => m.id === managerId);
      const res = await fetch(`${API_BASE}/api/relocation/${encodeURIComponent(request.requestId)}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ status:'Assigned to Manager',
          assignedManagerId: mgr.id, assignedManagerEmail: mgr.email, assignedManagerName: mgr.name }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      onSaved(); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
      backdropFilter:'blur(14px)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:22, padding:26, width:400, maxWidth:'94vw',
        boxShadow:'0 32px 80px rgba(0,0,0,.22)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}>
        <div style={{ fontSize:17, fontWeight:800, color:'#1c1c1e', marginBottom:4 }}>Assign to Manager</div>
        <div style={{ fontSize:12, color:'#8E8E93', marginBottom:18 }}>
          {request.requestId} · {request.userName}
        </div>

        <label style={{ fontSize:11, fontWeight:600, color:'#48484a', display:'block', marginBottom:6 }}>
          Select Manager *
        </label>
        <select value={managerId} onChange={e=>setManagerId(e.target.value)} style={{
          width:'100%', padding:'11px 12px', borderRadius:12,
          border:'1.5px solid rgba(0,0,0,.1)', fontSize:13, fontFamily:'inherit',
          background:'#fafafa', outline:'none', marginBottom:14,
        }}>
          <option value="">— Choose a Manager —</option>
          {SERVICE_MANAGERS.map(m => (
            <option key={m.id} value={m.id}>{m.name} · {m.region}</option>
          ))}
        </select>

        {error && <div style={{ fontSize:12, color:'#FF3B30', padding:'9px 12px',
          background:'rgba(255,59,48,.05)', borderRadius:9, marginBottom:12 }}>{error}</div>}

        <button onClick={handleSave} disabled={saving} style={{
          width:'100%', padding:13, borderRadius:13, border:'none',
          background: saving?'#c7c7cc':'linear-gradient(135deg,#007AFF,#0056d3)',
          color:'#fff', fontWeight:700, fontSize:14, cursor:saving?'wait':'pointer',
          fontFamily:'inherit', boxShadow: saving?'none':'0 4px 14px rgba(0,122,255,.3)',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
        }}>
          {saving ? (
            <><div style={{ width:14,height:14,borderRadius:'50%',border:'2px solid rgba(255,255,255,.3)',
              borderTopColor:'#fff',animation:'spin .6s linear infinite'}}/> Assigning…</>
          ) : '👤 Assign Manager'}
        </button>
        <button onClick={onClose} style={{ width:'100%',marginTop:10,padding:11,borderRadius:12,
          border:'1.5px solid rgba(0,0,0,.09)',background:'#fff',color:'#636366',
          fontWeight:600,fontSize:13,cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Request Detail Modal ──────────────────────────────────────────────────────
function RequestDetail({ request, onClose, onAssign }) {
  const cfg = relocStatusCfg(request.status);
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
      backdropFilter:'blur(16px)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:24, padding:26, width:540, maxWidth:'95vw',
        maxHeight:'88vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.22)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:10, color:'#aeaeb2', fontFamily:'monospace', fontWeight:700,
              letterSpacing:.8, marginBottom:6 }}>{request.requestId}</div>
            <div style={{ fontSize:20, fontWeight:800, color:'#1c1c1e', lineHeight:1.2 }}>
              🚚 {request.userName}
            </div>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:'50%', border:'none',
            background:'rgba(0,0,0,.07)', cursor:'pointer', fontSize:16, color:'#636366' }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}><StatusBadge status={request.status}/></div>

        {/* Route visualiser */}
        {(request.currentAddress || request.relocationAddress) && (
          <div style={{ background:'rgba(0,122,255,.04)', borderRadius:14,
            border:'1px solid rgba(0,122,255,.1)', padding:'14px 16px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#007AFF', letterSpacing:.6,
              textTransform:'uppercase', marginBottom:10 }}>Relocation Route</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ flex:1, background:'#fff', borderRadius:10, padding:'10px 12px',
                border:'1px solid rgba(0,0,0,.07)' }}>
                <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, marginBottom:3 }}>FROM</div>
                <div style={{ fontSize:12, fontWeight:700, color:'#1c1c1e' }}>
                  {request.currentAddress || '—'}
                </div>
              </div>
              <div style={{ fontSize:18 }}>→</div>
              <div style={{ flex:1, background:'#fff', borderRadius:10, padding:'10px 12px',
                border:'1px solid rgba(0,0,0,.07)' }}>
                <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, marginBottom:3 }}>TO</div>
                <div style={{ fontSize:12, fontWeight:700, color:'#1c1c1e' }}>
                  {request.relocationAddress || '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info grid */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
          {[
            ['Contact',          request.contactNumber || '—'],
            ['Created',          fmtDate(request.createdAt)],
            ['Updated',          fmtDate(request.updatedAt)],
            ['Assigned Manager', request.assignedManagerName || 'Not assigned'],
            ['Assigned CE',      request.assignedCEName      || 'Not assigned'],
          ].map(([l,v]) => (
            <div key={l} style={{ background:'rgba(0,0,0,.025)', borderRadius:11, padding:'10px 12px' }}>
              <div style={{ fontSize:10, color:'#8E8E93', fontWeight:600, marginBottom:3 }}>{l}</div>
              <div style={{ fontSize:12, fontWeight:700, color:'#1c1c1e', wordBreak:'break-word' }}>{v}</div>
            </div>
          ))}
        </div>

        {(!request.status || request.status === 'Pending') && (
          <button onClick={onAssign} style={{
            width:'100%', padding:13, borderRadius:13, border:'none',
            background:'linear-gradient(135deg,#007AFF,#0056d3)', color:'#fff',
            fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit',
            boxShadow:'0 4px 14px rgba(0,122,255,.3)', marginBottom:10,
          }}>👤 Assign to Manager</button>
        )}
        <button onClick={onClose} style={{ width:'100%', padding:11, borderRadius:12,
          border:'1.5px solid rgba(0,0,0,.09)', background:'#fff', color:'#636366',
          fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>Close</button>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function RelocationPage() {
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filter,    setFilter]    = useState('All');
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState(null);
  const [assigning, setAssigning] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/relocation`);
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const counts = { All: requests.length };
  RELOCATION_STATUSES.forEach(s => {
    counts[s.id] = requests.filter(r => (r.status||'Pending') === s.id).length;
  });

  const pending = counts['Pending'] || 0;

  const filtered = requests
    .filter(r => filter==='All' || (r.status||'Pending')===filter)
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (r.userName||'').toLowerCase().includes(q) ||
        (r.requestId||'').toLowerCase().includes(q) ||
        (r.currentAddress||'').toLowerCase().includes(q) ||
        (r.relocationAddress||'').toLowerCase().includes(q);
    });

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:960 }}>
      <style>{STYLES}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{ height:40, objectFit:'contain', marginBottom:10, display:'block' }}/>
          <h1 style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, margin:0 }}>
            🚚 Relocation Requests
          </h1>
          <p style={{ color:'#8E8E93', fontSize:13, marginTop:4, margin:'4px 0 0' }}>
            {requests.length} request{requests.length!==1?'s':''} total
            {pending > 0 && <span style={{ color:'#FF9F0A', fontWeight:700 }}> · {pending} pending assignment</span>}
          </p>
        </div>
        <button onClick={fetchRequests} disabled={loading} style={{
          display:'inline-flex', alignItems:'center', gap:7, padding:'9px 18px', borderRadius:12,
          border:'none', background:'linear-gradient(135deg,#007AFF,#0056d3)', color:'#fff',
          fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit',
          boxShadow:'0 4px 14px rgba(0,122,255,.25)', opacity:loading?.65:1,
        }}>
          <span style={loading?{animation:'spin .8s linear infinite',display:'flex'}:{display:'flex'}}>↻</span>
          {loading?'Loading…':'Refresh'}
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:12, marginBottom:20 }}>
        <KPICard label="Total" value={requests.length} color="#1c1c1e" icon="📋" delay=".0s"
          active={filter==='All'} onClick={()=>setFilter('All')}/>
        {RELOCATION_STATUSES.map((s,i) => (
          <KPICard key={s.id} label={s.id} value={counts[s.id]||0} color={s.color} icon={s.icon}
            delay={`${(i+1)*.05}s`} active={filter===s.id} onClick={()=>setFilter(f=>f===s.id?'All':s.id)}/>
        ))}
      </div>

      {/* ── Search + filter ── */}
      <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:4, background:'rgba(0,0,0,.04)', borderRadius:12, padding:3, flexWrap:'wrap' }}>
          {['All', ...RELOCATION_STATUSES.map(s=>s.id)].map(f => (
            <button key={f} onClick={()=>setFilter(f)} style={{
              padding:'7px 13px', borderRadius:9, border:'none', fontSize:11, fontWeight:filter===f?700:500,
              cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap',
              background: filter===f?'#fff':'transparent',
              color: filter===f?'#1c1c1e':'#8E8E93',
              boxShadow: filter===f?'0 1px 6px rgba(0,0,0,.09)':'none',
            }}>
              {f}{counts[f]>0?` (${counts[f]})`:''}</button>
          ))}
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search by name, address, ID…"
          style={{ flex:1, minWidth:180, padding:'9px 14px', borderRadius:12,
            border:'1.5px solid rgba(0,0,0,.09)', fontSize:13, fontFamily:'inherit',
            background:'#fff', outline:'none', color:'#1c1c1e' }}/>
      </div>

      {/* ── List ── */}
      {loading && requests.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'#8E8E93' }}>
          <div style={{ width:28,height:28,borderRadius:'50%',border:'3px solid rgba(0,122,255,.12)',
            borderTopColor:'#007AFF',animation:'spin .7s linear infinite',margin:'0 auto 14px'}}/>
          <p style={{ fontSize:13 }}>Loading relocation requests…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'#fff',borderRadius:20,padding:'56px 24px',textAlign:'center',
          border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 10px rgba(0,0,0,.04)',color:'#8E8E93' }}>
          <div style={{ fontSize:48, marginBottom:14 }}>🚚</div>
          <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginBottom:6 }}>No requests found</div>
          <div style={{ fontSize:13 }}>
            {search ? 'Try a different search term' : 'No relocation requests in this category'}
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {filtered.map((r, i) => {
            const cfg = relocStatusCfg(r.status);
            const isPending = !r.status || r.status === 'Pending';
            return (
              <div key={r.requestId||i} className="reloc-row" onClick={()=>setSelected(r)} style={{
                background:'#fff', borderRadius:16, padding:'16px 20px',
                boxShadow: isPending?'0 2px 12px rgba(255,159,10,.12)':'0 1px 6px rgba(0,0,0,.06)',
                border: isPending?'1.5px solid rgba(255,159,10,.25)':'1px solid rgba(0,0,0,.06)',
                display:'flex', alignItems:'center', gap:14,
                animation:`fadeUp .3s ease ${i*.04}s both`,
              }}>
                {/* Status indicator */}
                <div style={{ width:44, height:44, borderRadius:13, flexShrink:0,
                  background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
                  {cfg.icon}
                </div>

                {/* Content */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5, flexWrap:'wrap' }}>
                    <span style={{ fontSize:10, color:'#aeaeb2', fontFamily:'monospace', fontWeight:700 }}>
                      {r.requestId}
                    </span>
                    <StatusBadge status={r.status}/>
                    {isPending && (
                      <span style={{ fontSize:9, fontWeight:800, color:'#FF9F0A',
                        background:'rgba(255,159,10,.1)', padding:'2px 8px', borderRadius:20 }}>
                        ⚡ Needs assignment
                      </span>
                    )}
                    {r.assignedManagerName && (
                      <span style={{ fontSize:9, fontWeight:600, color:'#007AFF',
                        background:'rgba(0,122,255,.08)', padding:'2px 8px', borderRadius:20 }}>
                        👤 {r.assignedManagerName}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1c1c1e', marginBottom:5 }}>
                    {r.userName}
                  </div>
                  <div style={{ fontSize:11, color:'#8E8E93', display:'flex', gap:12, flexWrap:'wrap' }}>
                    {(r.currentAddress || r.relocationAddress) && (
                      <span>
                        📍 {r.currentAddress?.split(',')[0]||'—'}
                        <span style={{ margin:'0 4px', color:'#C7C7CC' }}>→</span>
                        {r.relocationAddress?.split(',')[0]||'—'}
                      </span>
                    )}
                    {r.contactNumber && <span>📞 {r.contactNumber}</span>}
                    <span>🕐 {fmtDate(r.createdAt)}</span>
                  </div>
                </div>

                {/* Quick assign */}
                {isPending && (
                  <button onClick={e=>{e.stopPropagation();setAssigning(r);}} style={{
                    padding:'9px 16px', borderRadius:11, border:'none',
                    background:'linear-gradient(135deg,#007AFF,#0056d3)', color:'#fff',
                    fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'inherit', flexShrink:0,
                    boxShadow:'0 3px 10px rgba(0,122,255,.3)',
                  }}>Assign →</button>
                )}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                  <path d="M9 18l6-6-6-6" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <RequestDetail request={selected} onClose={()=>setSelected(null)}
          onAssign={()=>{setAssigning(selected);setSelected(null);}}/>
      )}
      {assigning && (
        <AssignManagerModal request={assigning} onClose={()=>setAssigning(null)} onSaved={fetchRequests}/>
      )}
    </div>
  );
}
